import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api, resolveAsset } from "../lib/api";
import { Users, Trophy, ArrowRight, X, Gamepad2 } from "lucide-react";

const DISCORD_URL = "https://discord.gg/nEvwBUtqAV";

function initials(n) {
  if (!n) return "?";
  return n.split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
}

function statusStyles(s) {
  switch (s) {
    case "live":     return "text-red-300 bg-red-500/15 border border-red-500/40";
    case "scheduled":return "text-blue-200 bg-blue-500/15 border border-blue-500/30";
    case "finished": return "text-emerald-200 bg-emerald-500/15 border border-emerald-500/30";
    default:         return "text-zinc-300 bg-white/5 border border-white/10";
  }
}

export default function TeamsPage() {
  const [all, setAll] = useState([]); // [{team, tournament}]
  const [q, setQ] = useState("");
  const [tournamentFilter, setTournamentFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      const { data: ts } = await api.get("/tournaments");
      const rows = [];
      await Promise.all(
        ts.map(async (t) => {
          const { data } = await api.get(`/tournaments/${t.id}`);
          data.teams.forEach((team) => rows.push({ team, tournament: t }));
        })
      );
      setAll(rows);
    })();
  }, []);

  // Apply filters first
  const filteredRows = all.filter(({ team, tournament }) => {
    if (tournamentFilter !== "all" && tournament.id !== tournamentFilter) return false;
    if (q && !team.name.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  // Group by tournament. Each tournament keeps its own sorted team list + unique
  // team aggregate (so team cards still show combined W/L when that same team
  // is registered elsewhere — keeps the old modal useful).
  const uniqueByName = useMemo(() => {
    const map = new Map();
    all.forEach(({ team, tournament }) => {
      const key = team.name.toLowerCase();
      const existing = map.get(key);
      if (existing) {
        existing.tournaments.push({ ...tournament, teamId: team.id });
        existing.wins += team.wins || 0;
        existing.losses += team.losses || 0;
      } else {
        map.set(key, {
          name: team.name,
          color: team.color,
          logo_url: team.logo_url,
          wins: team.wins || 0,
          losses: team.losses || 0,
          tournaments: [{ ...tournament, teamId: team.id }],
        });
      }
    });
    return map;
  }, [all]);

  const groupedByTournament = useMemo(() => {
    const groups = new Map();
    filteredRows.forEach(({ team, tournament }) => {
      const g = groups.get(tournament.id) || {
        tournament,
        teams: [],
      };
      const agg = uniqueByName.get(team.name.toLowerCase());
      g.teams.push({ team, agg });
      groups.set(tournament.id, g);
    });
    // Sort teams inside each group by wins desc
    for (const g of groups.values()) {
      g.teams.sort((a, b) => (b.team.wins || 0) - (a.team.wins || 0));
    }
    // Sort tournaments: live first, then scheduled, then finished, then draft
    const order = { live: 0, scheduled: 1, finished: 2, draft: 3 };
    return Array.from(groups.values()).sort(
      (a, b) => (order[a.tournament.status] ?? 9) - (order[b.tournament.status] ?? 9),
    );
  }, [filteredRows, uniqueByName]);

  const tournamentList = useMemo(() => {
    const seen = new Map();
    all.forEach(({ tournament }) => {
      if (!seen.has(tournament.id)) seen.set(tournament.id, tournament);
    });
    const order = { live: 0, scheduled: 1, finished: 2, draft: 3 };
    return Array.from(seen.values()).sort(
      (a, b) => (order[a.status] ?? 9) - (order[b.status] ?? 9),
    );
  }, [all]);

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-red-400 font-mono">Registered Squads</div>
          <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter mt-2">Teams</h1>
          <p className="text-zinc-400 mt-3 max-w-2xl">
            Meet the competitors battling in Spicy tournaments — grouped by event.
          </p>
        </div>

        <input
          data-testid="teams-search"
          placeholder="Search teams..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full md:max-w-md bg-black/50 border border-white/10 rounded-full px-5 py-3 focus:outline-none focus:border-red-500 mb-4"
        />

        {/* Tournament filter chips */}
        {tournamentList.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-3 mb-6 -mx-1 px-1">
            <button
              onClick={() => setTournamentFilter("all")}
              data-testid="teams-filter-all"
              className={`shrink-0 px-3.5 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest transition-all ${
                tournamentFilter === "all" ? "bg-red-600 text-white" : "glass-dark text-zinc-400 hover:text-white"
              }`}
            >
              All events
            </button>
            {tournamentList.map((t) => (
              <button
                key={t.id}
                onClick={() => setTournamentFilter(t.id)}
                data-testid={`teams-filter-${t.id}`}
                className={`shrink-0 px-3.5 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest transition-all flex items-center gap-2 ${
                  tournamentFilter === t.id ? "bg-red-600 text-white" : "glass-dark text-zinc-400 hover:text-white"
                }`}
              >
                {t.status === "live" && <span className="live-dot" />}
                {t.name}
              </button>
            ))}
          </div>
        )}

        {/* Grouped team cards */}
        {groupedByTournament.length === 0 ? (
          <div className="glass-dark rounded-2xl p-10 text-center text-zinc-500">
            No teams found.
          </div>
        ) : (
          <div className="space-y-10">
            {groupedByTournament.map(({ tournament, teams }) => (
              <section key={tournament.id} data-testid={`teams-group-${tournament.id}`}>
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <Link
                    to={`/tournaments/${tournament.id}`}
                    className="font-display font-bold text-xl md:text-2xl tracking-tight text-white hover:text-red-200 transition-colors"
                  >
                    {tournament.name}
                  </Link>
                  <span
                    className={`text-[9px] font-mono tracking-[0.25em] uppercase px-2 py-1 rounded-full flex items-center gap-1.5 ${statusStyles(tournament.status)}`}
                  >
                    {tournament.status === "live" && <span className="live-dot" />}
                    {tournament.status}
                  </span>
                  {tournament.game && (
                    <span className="text-[10px] font-mono tracking-widest uppercase text-zinc-500 flex items-center gap-1">
                      <Gamepad2 size={10} /> {tournament.game}
                    </span>
                  )}
                  <span className="text-[10px] font-mono tracking-widest uppercase text-zinc-500">
                    · {teams.length} {teams.length === 1 ? "team" : "teams"}
                  </span>
                  <Link
                    to={`/tournament/${tournament.id}/bracket`}
                    className="ml-auto text-[10px] font-mono tracking-[0.25em] uppercase text-red-300 hover:text-red-200 flex items-center gap-1"
                  >
                    View bracket <ArrowRight size={10} />
                  </Link>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {teams.map(({ team, agg }, i) => (
                    <button
                      key={team.id}
                      onClick={() => setSelected(agg)}
                      data-testid={`team-card-${tournament.id}-${i}`}
                      className="text-left glass-dark rounded-2xl p-5 hover:border-red-500/40 hover:shadow-[0_0_24px_rgba(230,0,0,0.22)] transition-all"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        {team.logo_url ? (
                          <img src={resolveAsset(team.logo_url)} alt="" className="h-14 w-14 rounded-full object-cover border border-white/10" />
                        ) : (
                          <div className="h-14 w-14 rounded-full flex items-center justify-center font-display font-bold text-lg border border-white/10"
                            style={{ background: team.color || "#E60000" }}>
                            {initials(team.name)}
                          </div>
                        )}
                        <div className="min-w-0">
                          <div className="font-display font-bold text-lg truncate">{team.name}</div>
                          <div className="text-xs text-zinc-500 font-mono uppercase tracking-widest">
                            Seed #{team.seed || "-"}
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 pt-3 border-t border-white/5">
                        <div><div className="text-[10px] text-zinc-500 uppercase tracking-widest font-mono">Wins</div><div className="font-mono font-bold text-emerald-400">{team.wins || 0}</div></div>
                        <div><div className="text-[10px] text-zinc-500 uppercase tracking-widest font-mono">Losses</div><div className="font-mono font-bold text-red-400">{team.losses || 0}</div></div>
                        <div><div className="text-[10px] text-zinc-500 uppercase tracking-widest font-mono">Pts</div><div className="font-mono font-bold text-red-300">{(team.wins || 0) * 3}</div></div>
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <div
          onClick={() => setSelected(null)}
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            data-testid="team-profile-modal"
            className="glass-red-glow rounded-2xl p-8 max-w-lg w-full relative"
          >
            <button
              onClick={() => setSelected(null)}
              data-testid="close-team-modal"
              className="absolute top-4 right-4 h-8 w-8 rounded-full glass-dark flex items-center justify-center hover:text-red-400"
            >
              <X size={14} />
            </button>
            <div className="flex items-center gap-4 mb-6">
              {selected.logo_url ? (
                <img src={resolveAsset(selected.logo_url)} alt="" className="h-20 w-20 rounded-full object-cover border border-white/10" />
              ) : (
                <div className="h-20 w-20 rounded-full flex items-center justify-center font-display font-bold text-2xl" style={{ background: selected.color || "#E60000" }}>
                  {initials(selected.name)}
                </div>
              )}
              <div>
                <h3 className="font-display font-black text-3xl tracking-tight">{selected.name}</h3>
                <div className="text-sm text-zinc-400">
                  {selected.wins}W · {selected.losses}L · {selected.wins * 3} pts
                </div>
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest font-mono text-zinc-500 mb-3">
                Tournaments
              </div>
              <div className="space-y-2">
                {selected.tournaments.map((t) => (
                  <Link
                    key={t.id + t.teamId}
                    to={`/tournaments/${t.id}`}
                    onClick={() => setSelected(null)}
                    className="glass-dark rounded-xl p-3 flex items-center justify-between hover:border-red-500/40"
                  >
                    <div>
                      <div className="font-semibold text-sm">{t.name}</div>
                      <div className="text-xs text-zinc-500 uppercase tracking-widest font-mono">{t.status}</div>
                    </div>
                    <ArrowRight size={14} className="text-red-400" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer discordUrl={DISCORD_URL} />
    </div>
  );
}
